<template>
    <div class="form-group">
        <div class="wrapper">
            <div
                class="shadow"
                :style="cssProps"
                ref="shadowInput"
                v-html="shadowInputText"
            ></div>
            <textarea
                :id="name"
                :name="name"
                :value="textVal"
                :focus="focus"
                :class="{ filled: textVal.length > 0, error }"
                :style="cssProps"
                ref="textArea"
                :required="required"
                :disabled="disabled"
                :maxlength="maxlength"
                :rows="rows"
                @input="updateMessage"
                @change="updateMessage"
                @keydown="newLineHandler"
                @focus="toggleInputFocus(true)"
                @blur="toggleInputFocus(false)"
                autocomplete="off"
            />
            <InputLabel
                :active="inputFocus || textVal.length > 0"
                :name="name"
                :error="error"
                :disabled="disabled"
                >{{ label }}</InputLabel
            >
            <div class="top-bg" :class="{ 'top-bg--disabled': disabled }"></div>
        </div>
        <InputHint
            :error="error"
            :hint="hint"
            :required="required"
            :showOptional="showOptional"
        />
    </div>
</template>

<script>
import InputHint from "./InputHint";
import InputLabel from "./InputLabel";

export default {
  name: "TextArea",
  components: {
    InputHint,
    InputLabel
  },
  props: {
    value: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    focus: {
      type: Boolean,
      default: false
    },
    hint: String,
    error: String,
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showOptional: {
      type: Boolean,
      default: false
    },
    visibleLines: {
      type: Number,
      default: 2
    },
    maxHeight: {
      type: Number,
      default: 500
    },
    requireShiftEnter: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      required: false,
      default: null
    },
    rows: {
      type: Number,
      required: false,
      default: null
    }
  },
  computed: {
    cssProps() {
      return {
        "--font-size": `${this.fontSize}px`,
        "--line-height": `${this.lineHeight}px`,
        "--height": `${this.height}px`,
        "--min-height": `${this.defaultHeight}px`,
        "--max-height": `${this.maxHeight}px`
      };
    },
    shadowInputText() {
      return this.newlineResolver(this.textVal);
    }
  },
  data() {
    const fontSize = 18,
      lineHeight = Math.ceil(fontSize * 1.33),
      singleLineHeight = lineHeight + 22,
      visibleLines = this.$props.visibleLines - 1,
      defaultHeight = singleLineHeight + visibleLines * lineHeight;

    return {
      inputFocus: this.focus,
      fontSize,
      lineHeight,
      defaultHeight,
      height: defaultHeight,
      textVal: this.$props.value
    };
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    newLineHandler(e) {
      if (e.keyCode === 13) {
        if (!this.requireShift || e.shiftKey) {
          this.updateHeight(this.height + this.lineHeight);
        } else {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    },
    onChange() {
      this.$emit("input", this.textVal);
      this.$el.dispatchEvent(new Event("input"));
    },
    onResize() {
      this.targetMessage(this.textVal);
    },
    setFocus() {
      this.$refs.textArea.focus();
    },
    targetMessage(value) {
      this.updateMessage({
        target: { value }
      });
    },
    toggleInputFocus(toggle) {
      this.inputFocus = toggle;
    },
    updateHeight(height) {
      const { textArea } = this.$refs;
      if (textArea) {
        const updateScroll =
                    textArea.scrollTop <
                    textArea.scrollHeight - textArea.clientHeight;
        this.height = Math.ceil(height);
        this.$nextTick(() => {
          if (updateScroll) this.updateScroll;
        });
      }
    },
    updateMessage(e) {
      const { shadowInput } = this.$refs;
      const { value } = e.target;
      const triggerOnChange = this.textVal !== value;
      if (value.trim().length <= 0) {
        this.updateHeight(this.defaultHeight);
      }
      this.textVal = value;
      this.$nextTick(() => {
        if (triggerOnChange) this.onChange();
        const shadowHeight = shadowInput.offsetHeight;
        if (shadowHeight !== this.height) {
          this.updateHeight(shadowHeight + 1);
        }
      });
    },
    updateScroll() {
      const { textArea } = this.$refs;
      textArea.scrollTop = textArea.scrollHeight - textArea.clientHeight;
    },
    newlineResolver(str) {
      if (str) {
        if (str.endsWith("\n")) str += "a";
        return str.replace(/\n/g, "<br />");
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.textVal = newVal;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/style/_variables.scss";
@import "~@/style/_fonts.scss";

$fontSize: var(--font-size);
$lineHeight: var(--line-height);
$height: var(--height);
$minHeight: var(--min-height);
$maxHeight: var(--max-height);

.form-group {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: $padding-xl;

    &.question-note {
        .top-bg,
        textarea {
            background-color: $yellow-light;
        }
    }
}

.wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    padding-top: $padding-input-top;

    .shadow {
        position: absolute;
        width: 100%;
        left: 0;
        top: 0;
        padding: 0px $padding-input-left $padding-input-bottom;
        box-sizing: border-box;
        font-weight: 400;
        font-size: $fontSize;
        line-height: $lineHeight;
        vertical-align: middle;
        min-height: $minHeight;
        pointer-events: none;
        word-wrap: break-word;
        visibility: hidden;
    }

    .top-bg {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: calc(#{$padding-input-top} + 2px);
        background-color: $white;
        border-top-left-radius: $border-radius-md;
        border-top-right-radius: $border-radius-md;
        border: 1px solid $border-divide;
        border-bottom: none;
        box-sizing: border-box;
        transition: 0.2s ease-out;

        &--disabled {
            cursor: not-allowed;
            background-color: $background-gray;
        }
    }

    textarea {
        position: relative;
        width: 100%;
        height: $height;
        resize: none;
        overflow-y: auto;
        padding: 0px $padding-input-left $padding-input-bottom;
        box-sizing: border-box;
        color: $black;
        font-weight: 400;
        font-size: $fontSize;
        line-height: $lineHeight;
        vertical-align: middle;
        background-color: $white;
        outline: 0;
        border: 1px solid $border-divide;
        border-top: none;
        border-bottom-left-radius: $border-radius-md;
        border-bottom-right-radius: $border-radius-md;
        max-height: $maxHeight;
        caret-color: $blue-navy;
        transition: 0.2s ease-out;
        transition-property: border-color, background;
        box-shadow: none;
        height: 100%;
        font-family: $system-font;

        &:focus {
            border-color: $blue-navy;
            background-color: $white;

            & ~ .top-bg {
                border-color: $blue-navy;
                background-color: $white;
            }
        }

        &.error {
            border-color: $red-light;

            & ~ .top-bg {
                border-color: $red-light;
            }
        }

        &:disabled {
            cursor: not-allowed;
            background-color: $background-gray;
        }
    }
}
</style>
